import {
    GridColumnVisibilityModel,
    GridFilterModel,
    GridRowId,
    GridSortItem,
    GridSortModel,
    getGridDateOperators,
    GridFilterItem,
    GridColDef,
    GridSingleSelectColDef,
} from "@mui/x-data-grid-premium";
import { IEntityPageRequest } from "@shared/mirage/core/contracts/IEntityPageRequest";
import { Filter, FilterItemOperator, FilterOperator, Order, Where, WhereField, WhereFilterOperator } from "@shared/mirage/entityTable/Filter";
import { BaseColumnBinding, EntityBinding, EntityBindingColumns, Relations } from "@shared/mirage/metadata/BaseEntityRecordBinding";
import { ITableState, ModifyView } from "./EntityTable";
import { GridApiPremium } from "@mui/x-data-grid-premium/models/gridApiPremium";
import cellWithPopover from "./components/cellWithPopover";
import _, { cloneDeep } from "lodash";
import { EntityFieldAccessor } from "@shared/mirage/helpers/EntityFieldAccessor";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import InputBetweenDates from "./components/InputBetweenDates";
import NumberFormatter from "@shared/mirage/helpers/NumberFormatter";
import React from "react";
import { IUserTablePreferences, PredefinedEntityTableView } from "@shared/mirage/services/IUserPreferencesService";
import { EntityTableBooleanCell } from "./components/EntityTableBooleanCell";
import { BaseOption, Field, FlexibleOptionList, RuleGroupTypeIC, RuleType } from "react-querybuilder";
import { EntityTableHelper } from "@shared/mirage/helpers/EntityTableHelper";
export class EntityHelper {
    public static preparePageRequest(tableState: ITableState, filter: Filter, columns: GridColDef[], fetchAllColumns?: boolean, modifyView?: ModifyView): IEntityPageRequest {
        const pageRequest: IEntityPageRequest = {
            select: EntityHelper.prepareColumnsObject(columns, tableState.columnVisibilityModel, fetchAllColumns),
            pageSize: tableState.pageSize,
            offset: EntityHelper.calculateOffset(tableState.pageIndex, tableState.pageSize),
            filter: filter,
            order: EntityHelper.prepareSortingObject(tableState.sortModel),
            advancedFilter: EntityHelper.convertAdvancedQueryBuilderToFilter(this.getCurrentAdvanceQuery(tableState, modifyView)),
        };

        return pageRequest;
    }
    public static prepareFilterObject(filterModel: GridFilterModel, columns?: readonly GridColDef[], quickSearchColumns?: string[]) {
        const operator = filterModel.logicOperator?.toUpperCase() || "AND";
        const items = filterModel.items;
        const whereItems = items.map((item) => {
            const columnOperator = item.operator || "=";
            let value = item.value;
            if (item.operator === "isEmpty" || item.operator === "isNotEmpty") {
                value = true;
            }
            return {
                [item.field]: {
                    [columnOperator]: value,
                },
            };
        });
        const quickFilterValues: { [x: string]: { contains: string } }[] = [];

        if (filterModel.quickFilterValues?.length) {
            if (quickSearchColumns?.length) {
                quickSearchColumns.forEach((field) => {
                    filterModel.quickFilterValues?.forEach((quickFilterValue) => {
                        quickFilterValues.push({
                            [field]: {
                                contains: quickFilterValue as string,
                            },
                        });
                    });
                });
            } else if (columns?.length) {
                columns.forEach((col) => {
                    if (col.type !== "boolean" && col.type !== "date" && col.type !== "datetime") {
                        filterModel.quickFilterValues?.forEach((quickFilterValue) => {
                            quickFilterValues.push({
                                [col.field]: {
                                    contains: quickFilterValue as string,
                                },
                            });
                        });
                    }
                });
            }
        }

        let where = {} as Where;
        if (whereItems.length === 0) {
            if (quickFilterValues.length) {
                where = { OR: quickFilterValues } as Where;
            }
        } else {
            where = { [operator]: whereItems } as Where;
        }
        const filter = { parts: [], where } as Filter;
        return filter;
    }
    public static prepareSortingObject(sortItems: GridSortItem[]) {
        const sortObject: Order = {} as Order;
        (sortItems || []).forEach((sortItem) => {
            sortObject[sortItem.field] = sortItem.sort?.toLowerCase();
        });
        return sortObject;
    }
    private static prepareColumnsObject(columns: GridColDef[], columnVisibilityModel?: GridColumnVisibilityModel, fetchAllColumns?: boolean) {
        const obj = {} as { [columnName: string]: boolean };
        if (fetchAllColumns) {
            return obj;
        }
        const idColumnExists = columns.some((el) => el.field.toUpperCase() === "ID");
        const baseColumns = ["createdTime", "lastModifiedTime", "createdByUserId", "lastModifiedByUserId"];
        columns.forEach((column) => {
            if (columnVisibilityModel && columnVisibilityModel[column.field] === false && !baseColumns.includes(column.field)) {
                return;
            }
            obj[column.field] = true;
        });
        if (!idColumnExists) {
            // If ID column is not present, add it to the select (fix for public portal)
            obj["id"] = true;
        }
        return obj;
    }
    public static calculateOffset(pageIndex: number, pageSize: number) {
        return pageIndex > 0 ? pageIndex * pageSize : 0;
    }
    public static normaliseColumnsWidth = (columns: readonly GridColDef[], bindingColumns?: EntityBindingColumns<any>) => {
        return columns.map((column) => {
            const bindingColumn = bindingColumns?.[column.field];
            if (!column.flex && !column.width) {
                column.flex = 1;
            }
            return column;
        });
    };
    public static createSortModel(order: Order | undefined, sortModel: GridSortModel | undefined): GridSortItem[] {
        if (!order || !Object.keys(order).length) {
            if (sortModel) {
                return sortModel;
            } else {
                return [];
            }
        }
        const sortItems: GridSortItem[] = Object.keys(order).map((key) => {
            return {
                field: key,
                sort: order[key]?.toLowerCase() == "desc" ? "desc" : "asc",
            };
        });
        return sortItems;
    }
    public static getPageIndex(initial: IEntityPageRequest | undefined): number {
        if (!initial) {
            return 0;
        }
        if (typeof initial.offset === "undefined") {
            return 0;
        }
        if (initial.offset === 0) {
            return 0;
        }
        if (initial.pageSize === 0) {
            return 0;
        }
        return initial.offset / initial.pageSize;
    }

    public static setLookupTypeForRelationColumns(columns: readonly GridColDef[], relations: Relations | undefined) {
        const relationColumns = relations ? Object.keys(relations) : [];
        columns.forEach((column) => {
            if (relationColumns.includes(column.field)) {
                column.type = "lookup";
            }
        });
    }

    private static columnIsSingleSelectType(column: GridColDef): column is GridSingleSelectColDef {
        return column.type === "singleSelect";
    }

    public static setColumnPropsBasedOnType(columns: readonly GridColDef[], metaColumns?: EntityBindingColumns<any>) {
        return columns.map((column) => {
            if (this.columnIsSingleSelectType(column) && metaColumns) {
                column.valueOptions = column.valueOptions ? column.valueOptions : metaColumns[column.field]?.options || [];
            }
            if (column.type === "number") {
                column.headerAlign = "left";
            }
            if (column.type === "lookup") {
                column.valueGetter = (params) => {
                    if (!params.row[`${params.field}_relatedRecord`]?.id) {
                        return null;
                    }
                    return { id: params.row[`${params.field}_relatedRecord`]?.id, name: params.row[`${params.field}_relatedRecord`]?.name };
                };
                column.valueFormatter = (params) => {
                    return params.value?.name || "";
                };
            }
            if (column.type === "date") {
                column.valueFormatter = (params) => DateFormatter.formatToDate(params.value);
                column.filterOperators = [
                    ...getGridDateOperators(),
                    {
                        label: "between",
                        value: "between",
                        getApplyFilterFn: (filterItem: GridFilterItem) => {
                            if (!Array.isArray(filterItem.value) || filterItem.value.length !== 2) {
                                return null;
                            }
                            if (filterItem.value[0] == null || filterItem.value[1] == null) {
                                return null;
                            }

                            return ({ value }) => {
                                return value !== null && filterItem.value[0] <= value && value <= filterItem.value[1];
                            };
                        },
                        InputComponent: InputBetweenDates,
                    },
                ];
            }
            if (column.type === "boolean") {
                column.renderCell = EntityTableBooleanCell;
            }
            if (column.type === "dateTime") {
                column.valueFormatter = (params) => DateFormatter.formatToDateTimeWithSeconds(params.value);
            }
            if (column.type === "time") {
                column.valueFormatter = (params) => DateFormatter.formatTimeAmPm(params.value);
            }
            if (column.type === "currency") {
                column.valueFormatter = (params) => NumberFormatter.formatToGBP(params.value);
                column.align = "right";
            }
            if (column.type === "meters") {
                column.valueFormatter = (params) => NumberFormatter.formatMeters(params.value);
            }
            if (column.type === "area-square-meters") {
                column.valueFormatter = (params) => NumberFormatter.formatSquareMeters(params.value);
                column.align = "right";
            }
            if (column.type === "percent") {
                column.valueFormatter = (params) => NumberFormatter.formatPercent(params.value);
                column.align = "right";
            }
            if (column.type === "phone") {
                column.valueParser = (v) => (v ? Number(v) : null);
            }
            return column;
        });
    }

    public static setNotesColumn(columns: GridColDef[], onTextAreaSubmit?: (id: GridRowId, field: string, newVal: any) => Promise<void>, isEdit?: boolean) {
        columns.forEach((c, idx) => {
            if (c.type === "notes") {
                columns[idx] = { ...columns[idx], ...cellWithPopover(onTextAreaSubmit, isEdit) };
            }
        });
    }

    public static setRequiredColumns(columns: readonly GridColDef[], metaColumns?: EntityBindingColumns<unknown>) {
        if (!metaColumns || !columns) {
            return;
        }
        columns.forEach((column) => {
            const columnBinding: BaseColumnBinding = metaColumns[column.field as keyof EntityBindingColumns<unknown>];
            if (columnBinding?.isRequired) {
                const existingClassName = column.cellClassName || "";
                column.cellClassName = `${existingClassName} EntityTable__cell--required`.trim();
            }
        });
    }
    public static replaceFilterForColumnField(field: string, operator: string, value: any, apiRef: React.MutableRefObject<GridApiPremium>) {
        const currentFilters = apiRef.current.state.filter.filterModel.items;
        const filtersWithoutEditedColumn = currentFilters.filter((item) => item.field !== field);
        const newFilter = {
            field,
            id: `${field}-${operator}`,
            operator,
            value: value,
        };
        apiRef.current.upsertFilterItems([...filtersWithoutEditedColumn, newFilter]);
    }

    public static setBaseColumns(columns: GridColDef<any, any, any>[], disableFilterOnBaseColumn?: boolean) {
        const baseColumns = [
            {
                field: "createdTime",
                headerName: "Created Time",
                editable: false,
                flex: 0.5,
                type: "dateTime",
                filterable: !disableFilterOnBaseColumn,
            },
            {
                field: "lastModifiedTime",
                headerName: "Last Modified Time",
                editable: false,
                flex: 0.5,
                type: "dateTime",
                filterable: !disableFilterOnBaseColumn,
            },
            { field: "createdByUserId", headerName: "Created By", editable: false, flex: 0.5, type: "lookup", filterable: !disableFilterOnBaseColumn },
            { field: "lastModifiedByUserId", headerName: "Last Modified By", editable: false, flex: 0.5, type: "lookup", filterable: !disableFilterOnBaseColumn },
        ] as GridColDef<any, any, any>[];

        columns.forEach((column) => {
            baseColumns.forEach((baseColumn, idx) => {
                if (column.field === baseColumn.field) {
                    baseColumns.splice(idx, 1); // Remove column if that was declared initially in ET
                }
            });
        });

        columns.push(...baseColumns);
    }
    public static setColumnTypes(columns: GridColDef[], bindingColumns: EntityBindingColumns<any>) {
        columns.forEach((column) => {
            if (column?.type) {
                return;
            }
            if (!bindingColumns[column.field]?.contentType) {
                return;
            }
            if (bindingColumns[column.field].contentType === "CHECKBOX") {
                column.type = "boolean";
            } else if (bindingColumns[column.field].contentType === "DATE") {
                column.type = "date";
            } else if (bindingColumns[column.field].contentType === "DATETIME") {
                column.type = "dateTime";
            } else if (bindingColumns[column.field].contentType === "PICKLIST") {
                column.type = "singleSelect";
            }
        });
    }

    public static revertColumnsToUserPreferences(columns: GridColDef[], uiSettings: IUserTablePreferences) {
        const selectedView = uiSettings.predefinedEntityTableViews?.find((view) => view.id === uiSettings.selectedPredefinedEntityTableViewId);
        const storedColumns: any[] = selectedView?.columns?.length ? selectedView.columns : uiSettings.columns?.length ? uiSettings.columns : [];
        columns.forEach((column) => {
            const columnFromStorage = storedColumns.find((c: { field: string }) => c.field === column.field);
            if (columnFromStorage) {
                if (columnFromStorage.width) {
                    column.width = columnFromStorage.width;
                    column.flex = undefined;
                }
            }
        });
        const columnOrder: string[] = selectedView?.columnOrder ? selectedView.columnOrder : uiSettings.columnOrder ? uiSettings.columnOrder : [];
        columnOrder.forEach((field, idx) => {
            const column = columns.find((col) => col.field === field);
            if (column) {
                columns.splice(columns.indexOf(column), 1);
                columns.splice(idx, 0, column);
            }
        });
    }

    public static formatColumns(
        propsColumns: readonly GridColDef[],
        excludeBaseColumns?: boolean,
        setActionsColumn?: (columns: GridColDef<any, any, any>[]) => void,
        setPanelToggleColumn?: () => void,
        disableFilterOnBaseColumn?: boolean,
        bindingColumns?: EntityBindingColumns<any>,
        orderColumns?: GridSortItem[],
        applyFilterOnHeader?: (columns: GridColDef[]) => void,
        uiSettings?: IUserTablePreferences | null
    ) {
        const columns = EntityHelper.applyUserPreferencesColumns(propsColumns, uiSettings, bindingColumns);
        !excludeBaseColumns && EntityHelper.setBaseColumns(columns, disableFilterOnBaseColumn);
        EntityHelper.AddOrderColumnsToColumns(columns, orderColumns, bindingColumns);
        bindingColumns && EntityHelper.setColumnTypes(columns, bindingColumns);
        setActionsColumn && setActionsColumn(columns);
        applyFilterOnHeader && applyFilterOnHeader(columns);
        setPanelToggleColumn && setPanelToggleColumn();
        EntityHelper.setColumnPropsBasedOnType(columns, bindingColumns);
        uiSettings && EntityHelper.revertColumnsToUserPreferences(columns, uiSettings);
        return columns;
    }

    private static applyUserPreferencesColumns(
        propsColumns: readonly GridColDef[],
        uiSettings?: IUserTablePreferences | null,
        bindingColumns?: EntityBindingColumns<any>
    ): GridColDef[] {
        if (!uiSettings?.columns?.length && !uiSettings?.predefinedEntityTableViews?.length) {
            return EntityHelper.normaliseColumnsWidth(propsColumns, bindingColumns);
        }
        const selectedView = uiSettings?.predefinedEntityTableViews?.find((view) => view.id === uiSettings?.selectedPredefinedEntityTableViewId);
        if (selectedView && selectedView.columns?.length) {
            return EntityHelper.normaliseColumnsWidth(cloneDeep(selectedView.columns), bindingColumns);
        }
        if (uiSettings?.columns?.length) {
            //it is done later in -> uiSettings && EntityHelper.revertColumnsToUserPreferences(columns, uiSettings);
            return EntityHelper.normaliseColumnsWidth(cloneDeep(propsColumns), bindingColumns);
        }
        return EntityHelper.normaliseColumnsWidth(propsColumns, bindingColumns);
    }

    private static AddOrderColumnsToColumns(columns: GridColDef[], order?: GridSortItem[], bindingColumns?: EntityBindingColumns<any>) {
        if (!order?.length) {
            return;
        }
        const orderColumnsNotInColumns = EntityHelper.getOrderColumnsNotInColumnsList(columns, order);
        const orderColumns = orderColumnsNotInColumns.map((oc) => {
            const bindingColumn = bindingColumns?.[oc.field];
            if (bindingColumn) {
                return { field: oc.field, headerName: bindingColumn.label, ...EntityHelper.getColumnDefByBinding(bindingColumn) };
            }
            return { field: oc.field, headerName: oc.field, type: "string" };
        });
        columns.push(...orderColumns);
        return columns;
    }

    private static getOrderColumnsNotInColumnsList(columns: readonly GridColDef[], order?: GridSortItem[]) {
        if (!order?.length) {
            return [];
        }
        return order.filter((oc) => !columns.find((c) => c.field === oc.field));
    }

    public static prepareColumnVisibilityModel(
        columns: readonly GridColDef[],
        columnVisibilityModel?: GridColumnVisibilityModel,
        excludeBaseColumns?: boolean,
        order?: GridSortItem[]
    ) {
        const baseColumnVisibility = {
            createdTime: false,
            lastModifiedTime: false,
            createdByUserId: false,
            lastModifiedByUserId: false,
        };
        const orderColumnsNotInColumns = EntityHelper.getOrderColumnsNotInColumnsList(columns, order);
        const orderColumnsVisibility = orderColumnsNotInColumns.reduce((acc, oc) => ({ ...acc, [oc.field]: false }), {});

        return {
            ...(excludeBaseColumns ? {} : baseColumnVisibility),
            ...(columnVisibilityModel || {}),
            ...orderColumnsVisibility,
        };
    }

    public static getColumnDefByBinding(col: BaseColumnBinding): Partial<GridColDef<any, any, any>> {
        if (col.contentType == "DATE") {
            return { type: "date", flex: 0.3 };
        }
        if (col.contentType == "DATETIME") {
            return { type: "dateTime", flex: 0.3 };
        }
        if (col.contentType == "TIME") {
            return { type: "time", flex: 0.3 };
        }
        if (col.contentType == "TEXT") {
            return { flex: 1 };
        }
        if (col.contentType == "TEXTAREA") {
            return { flex: 1.5 };
        }
        if (col.contentType == "CHECKBOX") {
            return { type: "boolean", flex: 0.1 };
        }
        if (col.contentType == "NUMBER" && col.dbColumnName == "UPRN") {
            return { flex: 0.4 };
        }
        if (col.contentType == "NUMBER") {
            return { flex: 0.3 };
        }
        if (col.contentType == "AUTOCOMPLETE" || col.contentType == "PICKLIST") {
            return { flex: 1 };
        }
        if (col.contentType == "LOOKUP") {
            return { flex: 1 };
        }
        return {};
    }
    public static toggleLimitToUserFilter(pageRequest: IEntityPageRequest, officerFieldName: string, limitToUser: number | undefined) {
        const pr = _.cloneDeep(pageRequest);
        if (!pr.filter.where || !pr.filter.where.AND) {
            pr.filter.where = { AND: [] as Where<any>[] };
        }
        const rootWhere = pr.filter.where.AND as Where<any>[];

        const alreadyFilteredIndex = rootWhere.findIndex((e: any) => e[officerFieldName]);
        if (limitToUser) {
            if (alreadyFilteredIndex === -1) {
                rootWhere.push({ [officerFieldName]: { equals: limitToUser } });
            }
        } else {
            if (alreadyFilteredIndex >= 0) {
                rootWhere.splice(alreadyFilteredIndex, 1);
            }
        }
        return pr;
    }
    public static getUserPreferencesKey(stateKey: string) {
        const hashIndex = stateKey.indexOf("#");
        if (hashIndex !== -1) {
            return stateKey.substring(0, hashIndex);
        }
        return stateKey;
    }

    public static prepareAdvancedQueryBuilderFields(metadata: EntityBinding<unknown>): Field[] {
        const { columns } = metadata;
        const columnEntries = Object.entries(columns).filter(([, column]) => !(column as BaseColumnBinding).hideForTemplate);
        const fields = columnEntries.map(([columnName, columnBinding]) => {
            const typedColumnBinding = columnBinding as BaseColumnBinding;

            let inputType: string = typedColumnBinding.contentType;
            let options: string[] = [];

            if (typedColumnBinding.contentType === "LOOKUP") {
                inputType = `${typedColumnBinding.contentType}:${typedColumnBinding.relatedObject}`;
            }
            if (typedColumnBinding.contentType === "PICKLIST") {
                inputType = `${typedColumnBinding.contentType}:${typedColumnBinding.picklistName}`;
                options = typedColumnBinding.options ?? [];
            }

            return {
                label: typedColumnBinding.templateLabel || typedColumnBinding.label,
                name: columnName,
                inputType,
                values: options as unknown as FlexibleOptionList<BaseOption<string>>,
            } as Field;
        });

        return fields;
    }

    public static getCurrentTableView(tableState: ITableState): PredefinedEntityTableView | null {
        const tableView = tableState.predefinedEntityTableViews.find((view) => view.id === tableState.selectedPredefinedEntityTableViewId);
        if (!tableView) {
            return null;
        }
        return tableView;
    }

    public static validateModifyViewName(name: string): string | null {
        if (name.length > EntityTableHelper.MAX_VIEW_NAME_LENGTH) {
            return `Name must be less than ${EntityTableHelper.MAX_VIEW_NAME_LENGTH} characters`;
        }
        if (name.length === 0) {
            return "Name cannot be empty";
        }
        return null;
    }

    public static getCurrentAdvanceQuery(tableState: ITableState, modifyView?: ModifyView): RuleGroupTypeIC<RuleType<string, string, any, string>, string> {
        if (modifyView?.query) {
            return modifyView.query;
        }
        const tableView = this.getCurrentTableView(tableState);
        if (!tableView) {
            return { rules: [] };
        }
        return tableView.clientQuery as RuleGroupTypeIC<RuleType<string, string, any, string>, string>;
    }

    public static formatViewNameToMaxLength(name: string | null | undefined, where: "toolbar" | "list") {
        const maxLengthMap = {
            toolbar: 30,
            list: 39,
        };
        const maxLength = maxLengthMap[where];
        if (!name) {
            return "";
        }
        const nameLength = name.length;
        const nameLengthRemainder = nameLength - maxLength;
        if (name.length > maxLength && nameLengthRemainder < 3) {
            return name;
        }
        return name.length > maxLength ? name.substring(0, maxLength) + "..." : name;
    }

    public static convertAdvancedQueryBuilderToFilter<T>(ruleGroup: RuleGroupTypeIC<RuleType<string, string, any, string>, string>): Filter<T> {
        const convertRuleGroup = (group: RuleGroupTypeIC<RuleType<string, string, any, string>, string>): Where<T> | undefined => {
            if (!Array.isArray(group.rules) || group.rules.length === 0) {
                return undefined;
            }
            const groupOperator: FilterOperator = group.rules.some((item) => typeof item === "string" && item.toLowerCase() === "or") ? FilterOperator.OR : FilterOperator.AND;
            const currentGroup: WhereFilterOperator<T> = { [groupOperator]: [] };
            let tempAndGroup: WhereFilterOperator<T> | null = null;

            group.rules.forEach((ruleOrOperator) => {
                if (typeof ruleOrOperator === "string") {
                    if (ruleOrOperator.toLowerCase() === "or" && tempAndGroup) {
                        currentGroup[groupOperator]?.push(tempAndGroup);
                        tempAndGroup = null;
                    }
                    return;
                }

                if (typeof ruleOrOperator === "object" && "rules" in ruleOrOperator) {
                    const nestedConverted = convertRuleGroup(ruleOrOperator as RuleGroupTypeIC<RuleType<string, string, any, string>, string>);
                    if (nestedConverted) {
                        if (groupOperator === FilterOperator.OR) {
                            currentGroup[groupOperator]?.push(nestedConverted);
                        } else {
                            if (!tempAndGroup) {
                                tempAndGroup = { [FilterOperator.AND]: [] };
                            }
                            tempAndGroup[FilterOperator.AND]?.push(nestedConverted);
                        }
                    }
                } else {
                    const rule = ruleOrOperator as RuleType<string, string, any, string>;
                    let lookupId: number | null = null;
                    if (typeof rule.value === "object" && rule.value?.id) {
                        lookupId = rule.value.id;
                    }
                    const ruleAsWhereField: WhereField<any> = {
                        [rule.field]: {
                            [mapQueryOperatorToKnownOperators(rule.operator)]: typeof lookupId === "number" ? lookupId : rule.value,
                        },
                    };

                    if (groupOperator === FilterOperator.OR) {
                        currentGroup[groupOperator]?.push(ruleAsWhereField);
                    } else {
                        if (!tempAndGroup) {
                            tempAndGroup = { [FilterOperator.AND]: [] };
                        }
                        tempAndGroup[FilterOperator.AND]?.push(ruleAsWhereField);
                    }
                }
            });

            // Push any remaining AND group if applicable
            if (tempAndGroup) {
                currentGroup[groupOperator]?.push(tempAndGroup);
            }

            return currentGroup;
        };

        return {
            parts: [],
            where: convertRuleGroup(ruleGroup),
        };
    }
}

const mapQueryOperatorToKnownOperators = (operator: string): FilterItemOperator => {
    switch (operator) {
        case "null":
            return "isNull";
        case "notNull":
            return "isNotNull";
        default:
            return operator as FilterItemOperator;
    }
};
export const toGridColumn = (col: BaseColumnBinding) => {
    return {
        field: col.fieldName,
        headerName: col.label,
        type: col.contentType.toLowerCase() === "checkbox" ? "boolean" : col.contentType.toLowerCase(),
    } as GridColDef;
};
export const fromRelatedProperty = (prop: string, header: string) => {
    return {
        field: prop,
        headerName: header,
        valueGetter: (params) => {
            return _.get(params.row, prop);
        },
    } as GridColDef;
};
export const fromExtendedRecord = <T>(property: (object: T) => void, columns: EntityBindingColumns<any>) => {
    const accessor = new EntityFieldAccessor<T>();
    const { propertyPath, column } = accessor.getFieldPath(property);
    if (propertyPath && column) {
        const binding = columns[column];
        if (binding) {
            return {
                field: propertyPath,
                headerName: binding.label,
                type: binding.contentType.toLowerCase(),
                valueGetter: (params) => {
                    return _.get(params.row, propertyPath);
                },
            } as GridColDef;
        }
    }
    return null;
};
